import React, { useState, useEffect } from 'react';
import { usePBX, CALL_STATUS } from '../../context/PBXContext';
import { useSIP, SIP_CALL_STATUS } from '../../context/SIPContext';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Phone,
  PhoneCall,
  PhoneOff,
  PhoneMissed,
  Mic,
  MicOff,
  User,
  Voicemail,
  Clock,
  Circle,
  PhoneIncoming,
  PhoneOutgoing,
  BookUser,
  Search,
  Globe,
  Radio,
} from 'lucide-react';

// DTMF dial pad buttons
const DTMF_KEYS = [
  ['1', '2', '3'],
  ['4', '5', '6'],
  ['7', '8', '9'],
  ['*', '0', '#'],
];

function ExternalCallPanel({ sip, externalNumber, setExternalNumber }) {
  const {
    registrationStatus, callStatus, currentCall,
    callDuration, isMuted,
    makeCall, answerCall, rejectCall, endCall,
    toggleMute, sendDTMF, formatDuration, externalCallLogs,
  } = sip;

  const isIdle = callStatus === 'idle';
  const isConnected = callStatus === 'connected';
  const isRinging = callStatus === 'ringing' || callStatus === 'dialing';
  const isIncoming = callStatus === 'incoming';

  const handleDTMF = (key) => {
    if (isConnected) {
      sendDTMF(key);
    }
    setExternalNumber(prev => prev + key);
  };

  const regColor = registrationStatus === 'registered' ? 'text-green-500' : registrationStatus === 'registering' ? 'text-yellow-500' : 'text-red-400';
  const regLabel = registrationStatus === 'registered' ? 'Connected' : registrationStatus === 'registering' ? 'Connecting...' : 'Disconnected';

  return (
    <div className="space-y-2" data-testid="external-call-panel">
      {/* SIP Registration Status */}
      <div className="flex items-center justify-between text-xs px-1">
        <span className="text-muted-foreground flex items-center gap-1">
          <Globe className="w-3 h-3" /> SIP
        </span>
        <span className={`font-medium ${regColor}`}>{regLabel}</span>
      </div>

      {/* Active External Call */}
      {!isIdle && currentCall && (
        <div className={`p-3 rounded-lg text-center border ${
          isConnected ? 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700'
            : 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-700'
        }`}>
          <p className="text-[11px] text-muted-foreground capitalize tracking-wide">{callStatus}</p>
          <p className="font-semibold text-base mt-0.5">{currentCall.displayName}</p>
          <p className="text-xs text-muted-foreground">{currentCall.number}</p>
          {isConnected && (
            <p className="font-mono text-xl mt-1 tabular-nums">{formatDuration(callDuration)}</p>
          )}
        </div>
      )}

      {/* Incoming external call */}
      {isIncoming && (
        <div className="flex gap-2">
          <Button className="flex-1 bg-green-500 hover:bg-green-600 h-9" onClick={answerCall} data-testid="ext-answer-btn">
            <PhoneCall className="w-4 h-4 mr-1" /> Answer
          </Button>
          <Button className="flex-1 h-9" variant="destructive" onClick={rejectCall} data-testid="ext-reject-btn">
            <PhoneOff className="w-4 h-4 mr-1" /> Reject
          </Button>
        </div>
      )}

      {/* In-call controls */}
      {(isConnected || isRinging) && (
        <div className="flex gap-2">
          <Button variant={isMuted ? 'destructive' : 'outline'} className="flex-1 h-9" onClick={toggleMute} data-testid="ext-mute-btn">
            {isMuted ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
          </Button>
          <Button variant="destructive" className="flex-1 h-9" onClick={() => endCall(true)} data-testid="ext-end-call-btn">
            <PhoneOff className="w-4 h-4 mr-1" /> End
          </Button>
        </div>
      )}

      {/* Dialer */}
      {isIdle && registrationStatus === 'registered' && (
        <Tabs defaultValue="dial" className="w-full">
          <TabsList className="grid w-full grid-cols-2 h-8">
            <TabsTrigger value="dial" className="text-xs" data-testid="ext-tab-dial">
              <Phone className="w-3 h-3 mr-1" />Dial
            </TabsTrigger>
            <TabsTrigger value="history" className="text-xs" data-testid="ext-tab-history">
              <Clock className="w-3 h-3 mr-1" />History
            </TabsTrigger>
          </TabsList>

          <TabsContent value="dial" className="mt-2 space-y-2">
            <Input
              className="h-10 text-center text-lg font-mono tracking-wider"
              placeholder="Enter number..."
              value={externalNumber}
              onChange={e => setExternalNumber(e.target.value)}
              data-testid="ext-dial-input"
            />
            {/* DTMF Pad */}
            <div className="grid grid-cols-3 gap-1">
              {DTMF_KEYS.flat().map(key => (
                <Button
                  key={key}
                  variant="outline"
                  className="h-10 text-base font-mono"
                  onClick={() => handleDTMF(key)}
                  data-testid={`dtmf-${key === '*' ? 'star' : key === '#' ? 'hash' : key}`}
                >
                  {key}
                </Button>
              ))}
            </div>
            <div className="flex gap-2">
              <Button
                className="flex-1 h-10 bg-green-500 hover:bg-green-600"
                onClick={() => { if (externalNumber.trim()) makeCall(externalNumber.trim()); }}
                disabled={!externalNumber.trim()}
                data-testid="ext-call-btn"
              >
                <PhoneCall className="w-4 h-4 mr-2" /> Call
              </Button>
              {externalNumber && (
                <Button variant="outline" className="h-10 px-3" onClick={() => setExternalNumber('')}>
                  Clear
                </Button>
              )}
            </div>
          </TabsContent>

          <TabsContent value="history" className="mt-2">
            <ScrollArea className="h-48">
              <div className="space-y-0.5 pr-1">
                {externalCallLogs.length === 0 ? (
                  <p className="text-xs text-center text-muted-foreground py-6">No external calls yet</p>
                ) : (
                  externalCallLogs.slice(0, 20).map((call, idx) => (
                    <div key={idx} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted/40">
                      {call.direction === 'outgoing'
                        ? <PhoneOutgoing className="w-3.5 h-3.5 text-blue-500 shrink-0" />
                        : <PhoneIncoming className="w-3.5 h-3.5 text-green-500 shrink-0" />
                      }
                      <span className="text-xs flex-1 truncate">{call.displayName || call.number}</span>
                      <span className="text-[11px] text-muted-foreground shrink-0">
                        {call.duration > 0 ? formatDuration(call.duration) : '—'}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>
          </TabsContent>
        </Tabs>
      )}

      {/* Not registered message */}
      {isIdle && registrationStatus !== 'registered' && registrationStatus !== 'registering' && (
        <div className="text-center py-3 text-muted-foreground">
          <Globe className="w-6 h-6 mx-auto mb-1.5 opacity-40" />
          <p className="text-xs font-medium">SIP Not Connected</p>
          <p className="text-[11px] mt-0.5">
            {registrationStatus === 'failed'
              ? 'Connection failed — check SIP credentials in Settings'
              : 'Configure SIP in Settings to enable external calls'}
          </p>
        </div>
      )}

      {registrationStatus === 'registering' && (
        <div className="text-center py-3 text-muted-foreground">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary mx-auto mb-1.5" />
          <p className="text-xs">Connecting to SIP server...</p>
        </div>
      )}
    </div>
  );
}

export default function Softphone({ embedded = false }) {
  const pbx = usePBX();
  const sip = useSIP();
  const { isParticipant } = useAuth();
  const [dialNumber, setDialNumber] = useState('');
  const [directorySearch, setDirectorySearch] = useState('');
  const [callMode, setCallMode] = useState(isParticipant() ? 'external' : 'internal'); // participants forced to external
  const [externalNumber, setExternalNumber] = useState('');

  // Auto-request microphone permission when Softphone mounts (user opens Phone tab)
  useEffect(() => {
    if (pbx && pbx.micPermission && pbx.micPermission !== 'granted') {
      pbx.requestMicPermission();
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!pbx) return null;

  const {
    myPhone, phoneNumbers, onlineExtensions,
    callStatus, currentCall, incomingCall,
    callDuration, isMuted, callLogs, voicemails,
    makeCall, answerCall, rejectCall, endCall, toggleMute,
    formatDuration, micPermission, requestMicPermission,
  } = pbx;

  const unreadVoicemails = voicemails.filter(
    v => !v.is_read && myPhone && v.to_extension === myPhone.extension
  ).length;

  const filteredContacts = phoneNumbers
    .filter(n => n.extension !== myPhone?.extension)
    .filter(n =>
      directorySearch === '' ||
      n.role_name.toLowerCase().includes(directorySearch.toLowerCase()) ||
      n.extension.includes(directorySearch)
    )
    .sort((a, b) => {
      const aOn = onlineExtensions.includes(a.extension);
      const bOn = onlineExtensions.includes(b.extension);
      if (aOn && !bOn) return -1;
      if (!aOn && bOn) return 1;
      return a.extension.localeCompare(b.extension);
    });

  // ── No phone assigned ──
  if (!myPhone) {
    const noPhoneContent = (
      <>
        {/* Show External SIP if enabled, even without internal extension */}
        {sip && sip.sipEnabled && (
          <div className="space-y-2 mb-3">
            <div className="flex items-center gap-2 mb-2">
              <Globe className="w-4 h-4 text-blue-500" />
              <span className="text-sm font-medium">External Calls (SIP)</span>
            </div>
            <ExternalCallPanel sip={sip} externalNumber={externalNumber} setExternalNumber={setExternalNumber} />
          </div>
        )}

        {(!sip || !sip.sipEnabled) && (
          <div className="text-center py-4 text-muted-foreground">
            <PhoneOff className="w-8 h-8 mx-auto mb-2 opacity-40" />
            <p className="text-sm font-medium">No extension assigned</p>
            <p className="text-xs mt-1">Ask your administrator to assign you a phone number.</p>
          </div>
        )}

        {sip && sip.sipEnabled && !isParticipant() && (
          <div className="border-t pt-2 mt-2">
            <p className="text-[10px] text-muted-foreground text-center">Internal PBX extension not assigned</p>
          </div>
        )}

        {phoneNumbers.length > 0 && !isParticipant() && (
            <div className="mt-3 border-t pt-3">
              <p className="text-[11px] font-medium text-muted-foreground mb-2 flex items-center gap-1">
                <BookUser className="w-3 h-3" /> Internal Call List
              </p>
              <ScrollArea className="h-48">
                <div className="space-y-0.5">
                  {phoneNumbers.map(n => (
                    <div key={n.extension} className="flex items-center gap-2 px-1 py-1 text-xs">
                      <Circle className={`w-2 h-2 shrink-0 ${onlineExtensions.includes(n.extension) ? 'fill-green-500 text-green-500' : 'fill-gray-300 text-muted-foreground'}`} />
                      <span className="font-mono w-10 shrink-0 text-muted-foreground">{n.extension}</span>
                      <span className="truncate">{n.role_name}</span>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>
        )}
      </>
    );

    if (embedded) {
      return <div data-testid="softphone-no-phone">{noPhoneContent}</div>;
    }

    return (
      <Card className="w-full" data-testid="softphone-no-phone">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Phone className="w-4 h-4" /> Phone
          </CardTitle>
        </CardHeader>
        <CardContent>{noPhoneContent}</CardContent>
      </Card>
    );
  }

  const phoneContent = (
    <>
      {/* Mic permission prompt */}
      {micPermission && micPermission !== 'granted' && (
        <div className={`mb-2 p-2.5 rounded-lg text-xs flex items-center gap-2 ${
          micPermission === 'denied'
            ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300'
            : 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-300'
        }`} data-testid="mic-permission-banner">
          {micPermission === 'denied' ? (
            <>
              <MicOff className="w-4 h-4 flex-shrink-0" />
              <span className="flex-1">Microphone blocked. Click retry or allow mic in browser settings (click lock icon in address bar).</span>
              <Button size="sm" variant="outline" className="h-6 text-[10px] px-2 shrink-0 border-red-300 dark:border-red-700 hover:bg-red-100 dark:hover:bg-red-900/30" onClick={requestMicPermission}
                data-testid="retry-mic-btn">
                Retry
              </Button>
            </>
          ) : (
            <>
              <Mic className="w-4 h-4 flex-shrink-0" />
              <span className="flex-1">Enable microphone to make and receive calls.</span>
              <Button size="sm" variant="outline" className="h-6 text-[10px] px-2 shrink-0" onClick={requestMicPermission}
                data-testid="enable-mic-btn">
                Enable Mic
              </Button>
            </>
          )}
        </div>
      )}

      {/* Header info */}
      <div className={embedded ? 'mb-2' : ''}>
        <div className="flex items-center justify-between">
          {!embedded && (
            <CardTitle className="text-base flex items-center gap-2">
              <Phone className="w-4 h-4 text-green-500" /> Phone
            </CardTitle>
          )}
          {embedded && (
            <p className="text-sm font-medium flex items-center gap-1.5">
              <Phone className="w-3.5 h-3.5 text-green-500" /> {myPhone.role_name}
            </p>
          )}
          <Badge variant="outline" className="text-xs font-mono">Ext. {myPhone.extension}</Badge>
        </div>
        {!embedded && <p className="text-xs text-muted-foreground mt-0.5">{myPhone.role_name}</p>}
      </div>

      <div className="space-y-2">
        {/* Mode Toggle: Internal / External — hidden for participants (external only) */}
        {sip && sip.sipEnabled && !isParticipant() && (
          <div className="flex rounded-lg border p-0.5 bg-muted/50" data-testid="call-mode-toggle">
            <button
              className={`flex-1 flex items-center justify-center gap-1 text-xs py-1.5 rounded-md transition-colors ${
                callMode === 'internal' ? 'bg-background shadow-sm font-medium' : 'text-muted-foreground hover:text-foreground'
              }`}
              onClick={() => setCallMode('internal')}
            >
              <Radio className="w-3 h-3" /> Internal
            </button>
            <button
              className={`flex-1 flex items-center justify-center gap-1 text-xs py-1.5 rounded-md transition-colors ${
                callMode === 'external' ? 'bg-background shadow-sm font-medium' : 'text-muted-foreground hover:text-foreground'
              }`}
              onClick={() => setCallMode('external')}
            >
              <Globe className="w-3 h-3" /> External
            </button>
          </div>
        )}

        {/* Active call display (Internal) */}
        {callMode === 'internal' && callStatus !== CALL_STATUS.IDLE && (
          <div className={`p-3 rounded-lg text-center border ${
            callStatus === CALL_STATUS.CONNECTED
              ? 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700'
              : 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-700'
          }`}>
            <p className="text-[11px] text-muted-foreground capitalize tracking-wide">{callStatus}</p>
            <p className="font-semibold text-base mt-0.5">{currentCall?.role || incomingCall?.role}</p>
            <p className="text-xs text-muted-foreground">Ext. {currentCall?.extension || incomingCall?.extension}</p>
            {callStatus === CALL_STATUS.CONNECTED && (
              <p className="font-mono text-xl mt-1 tabular-nums">{formatDuration(callDuration)}</p>
            )}
          </div>
        )}

        {/* Incoming call buttons (Internal) */}
        {callMode === 'internal' && callStatus === CALL_STATUS.INCOMING && (
          <div className="flex gap-2">
            <Button className="flex-1 bg-green-500 hover:bg-green-600 h-9" onClick={answerCall} data-testid="answer-btn">
              <PhoneCall className="w-4 h-4 mr-1" /> Answer
            </Button>
            <Button className="flex-1 h-9" variant="destructive" onClick={rejectCall} data-testid="reject-btn">
              <PhoneOff className="w-4 h-4 mr-1" /> Reject
            </Button>
          </div>
        )}

        {/* In-call controls (Internal) */}
        {callMode === 'internal' && (callStatus === CALL_STATUS.CONNECTED || callStatus === CALL_STATUS.DIALING || callStatus === CALL_STATUS.RINGING) && (
          <div className="flex gap-2">
            <Button variant={isMuted ? 'destructive' : 'outline'} className="flex-1 h-9" onClick={toggleMute} data-testid="mute-btn">
              {isMuted ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
            </Button>
            <Button variant="destructive" className="flex-1 h-9" onClick={() => endCall(true)} data-testid="end-call-btn">
              <PhoneOff className="w-4 h-4 mr-1" /> End
            </Button>
          </div>
        )}

        {/* ══════ EXTERNAL MODE ══════ */}
        {callMode === 'external' && sip && (
          <ExternalCallPanel sip={sip} externalNumber={externalNumber} setExternalNumber={setExternalNumber} />
        )}

        {/* ══════ INTERNAL MODE ══════ */}
        {callMode === 'internal' && (
        <Tabs defaultValue="dial" className="w-full">
          <TabsList className={`grid w-full h-8 ${isParticipant() ? 'grid-cols-2' : 'grid-cols-3'}`}>
            <TabsTrigger value="dial" className="text-xs" data-testid="softphone-tab-dial">
              <Phone className="w-3 h-3 mr-1" />Dial
            </TabsTrigger>
            {!isParticipant() && (
              <TabsTrigger value="directory" className="text-xs" data-testid="softphone-tab-directory">
                <BookUser className="w-3 h-3 mr-1" />Directory
              </TabsTrigger>
            )}
            <TabsTrigger value="history" className="text-xs" data-testid="softphone-tab-history">
              <Clock className="w-3 h-3 mr-1" />History
              {unreadVoicemails > 0 && (
                <span className="ml-1 bg-orange-500 text-white text-[9px] rounded-full w-3.5 h-3.5 flex items-center justify-center">
                  {unreadVoicemails}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

            {/* Dial tab */}
            <TabsContent value="dial" className="mt-2 space-y-2">
              {callStatus === CALL_STATUS.IDLE ? (
                <>
                  <Select value={dialNumber} onValueChange={setDialNumber}>
                    <SelectTrigger className="h-9" data-testid="dial-select">
                      <SelectValue placeholder="Select extension to call" />
                    </SelectTrigger>
                    <SelectContent className="max-h-60 overflow-y-auto z-[1200]">
                      {phoneNumbers
                        .filter(n => n.extension !== myPhone.extension)
                        .map(n => (
                          <SelectItem key={n.extension} value={n.extension}>
                            <div className="flex items-center gap-2">
                              <Circle className={`w-2 h-2 shrink-0 ${onlineExtensions.includes(n.extension) ? 'fill-green-500 text-green-500' : 'fill-gray-400 text-muted-foreground'}`} />
                              <span className="font-mono">{n.extension}</span>
                              <span className="text-muted-foreground">— {n.role_name}</span>
                            </div>
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <Button className="w-full h-9" onClick={() => makeCall(dialNumber)} disabled={!dialNumber} data-testid="call-btn">
                    <PhoneCall className="w-4 h-4 mr-2" /> Call
                  </Button>
                  {onlineExtensions.filter(e => e !== myPhone.extension).length > 0 && (
                    <div className="pt-1 border-t">
                      <p className="text-[11px] text-muted-foreground mb-1.5">Online now</p>
                      <div className="flex flex-wrap gap-1">
                        {phoneNumbers
                          .filter(n => onlineExtensions.includes(n.extension) && n.extension !== myPhone.extension)
                          .slice(0, 6)
                          .map(n => (
                            <Button key={n.extension} variant="outline" size="sm" className="text-xs h-7 px-2" onClick={() => makeCall(n.extension)}>
                              <Circle className="w-1.5 h-1.5 fill-green-500 text-green-500 mr-1" />
                              {n.extension}
                            </Button>
                          ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-xs text-center text-muted-foreground py-4">Call in progress…</p>
              )}
            </TabsContent>

            {/* Directory tab */}
            <TabsContent value="directory" className="mt-2 space-y-2" data-testid="softphone-directory">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                <Input
                  className="pl-8 h-8 text-xs"
                  placeholder="Search role or extension…"
                  value={directorySearch}
                  onChange={e => setDirectorySearch(e.target.value)}
                  data-testid="directory-search"
                />
              </div>
              <ScrollArea className="h-48">
                <div className="space-y-0.5 pr-1">
                  {filteredContacts.length === 0 ? (
                    <p className="text-xs text-center text-muted-foreground py-4">No contacts found</p>
                  ) : (
                    filteredContacts.map(n => {
                      const isOnline = onlineExtensions.includes(n.extension);
                      return (
                        <div key={n.extension} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted/60 group" data-testid={`contact-${n.extension}`}>
                          <Circle className={`w-2 h-2 shrink-0 ${isOnline ? 'fill-green-500 text-green-500' : 'fill-gray-300 text-muted-foreground'}`} />
                          <span className="font-mono text-xs w-10 shrink-0 text-muted-foreground">{n.extension}</span>
                          <span className="text-xs flex-1 truncate">{n.role_name}</span>
                          <Button
                            size="sm"
                            variant={isOnline ? 'default' : 'outline'}
                            className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                            onClick={() => makeCall(n.extension)}
                            disabled={callStatus !== CALL_STATUS.IDLE}
                            title={`Call ${n.role_name}`}
                          >
                            <PhoneCall className="w-3 h-3" />
                          </Button>
                        </div>
                      );
                    })
                  )}
                </div>
              </ScrollArea>
              <p className="text-[10px] text-muted-foreground text-center">
                {filteredContacts.filter(n => onlineExtensions.includes(n.extension)).length} online · {filteredContacts.length} total
              </p>
            </TabsContent>

            {/* History tab */}
            <TabsContent value="history" className="mt-2 space-y-2" data-testid="softphone-history">
              {unreadVoicemails > 0 && (
                <div className="flex items-center gap-2 px-3 py-2 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700 rounded-lg">
                  <Voicemail className="w-4 h-4 text-orange-500 shrink-0" />
                  <span className="text-xs font-medium">{unreadVoicemails} new voicemail{unreadVoicemails > 1 ? 's' : ''}</span>
                </div>
              )}
              <ScrollArea className="h-48">
                <div className="space-y-0.5 pr-1">
                  {callLogs.length === 0 ? (
                    <p className="text-xs text-center text-muted-foreground py-6">No calls yet</p>
                  ) : (
                    callLogs.slice(0, 20).map(call => {
                      const isOut = myPhone && call.from_extension === myPhone.extension;
                      return (
                        <div key={call.call_id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted/40">
                          {call.status === 'missed'
                            ? <PhoneMissed className="w-3.5 h-3.5 text-red-500 shrink-0" />
                            : isOut
                              ? <PhoneOutgoing className="w-3.5 h-3.5 text-blue-500 shrink-0" />
                              : <PhoneIncoming className="w-3.5 h-3.5 text-green-500 shrink-0" />
                          }
                          <span className="text-xs flex-1 truncate">{isOut ? call.to_role : call.from_role}</span>
                          <span className="text-[11px] text-muted-foreground shrink-0">
                            {call.duration_seconds ? formatDuration(call.duration_seconds) : '—'}
                          </span>
                        </div>
                      );
                    })
                  )}
                </div>
              </ScrollArea>
            </TabsContent>
          </Tabs>
        )}
      </div>
    </>
  );

  if (embedded) {
    return (
      <>
        <div data-testid="softphone">
          {phoneContent}
        </div>
        {/* Incoming call dialog */}
        <Dialog open={callStatus === CALL_STATUS.INCOMING} onOpenChange={() => {}}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <PhoneIncoming className="w-5 h-5 text-green-500 animate-pulse" />
                Incoming Call
              </DialogTitle>
            </DialogHeader>
            <div className="text-center py-4">
              <User className="w-14 h-14 mx-auto mb-3 p-3 bg-muted rounded-full" />
              <p className="text-lg font-bold">{incomingCall?.role}</p>
              <p className="text-sm text-muted-foreground">Extension {incomingCall?.extension}</p>
            </div>
            <div className="flex gap-2">
              <Button className="flex-1 bg-green-500 hover:bg-green-600" onClick={answerCall}>
                <PhoneCall className="w-4 h-4 mr-2" /> Answer
              </Button>
              <Button className="flex-1" variant="destructive" onClick={rejectCall}>
                <PhoneOff className="w-4 h-4 mr-2" /> Reject
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <>
      <Card className="w-full" data-testid="softphone">
        <CardHeader className="pb-1">
          {phoneContent}
        </CardHeader>
      </Card>

      {/* Incoming call dialog (in-page confirmation) */}
      <Dialog open={callStatus === CALL_STATUS.INCOMING} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PhoneIncoming className="w-5 h-5 text-green-500 animate-pulse" />
              Incoming Call
            </DialogTitle>
          </DialogHeader>
          <div className="text-center py-4">
            <User className="w-14 h-14 mx-auto mb-3 p-3 bg-muted rounded-full" />
            <p className="text-lg font-bold">{incomingCall?.role}</p>
            <p className="text-sm text-muted-foreground">Extension {incomingCall?.extension}</p>
          </div>
          <div className="flex gap-2">
            <Button className="flex-1 bg-green-500 hover:bg-green-600" onClick={answerCall}>
              <PhoneCall className="w-4 h-4 mr-2" /> Answer
            </Button>
            <Button className="flex-1" variant="destructive" onClick={rejectCall}>
              <PhoneOff className="w-4 h-4 mr-2" /> Reject
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
